import { useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { errorMessage } from '../api/errors.js'
import { AppShell } from '../components/layout/AppShell.js'
import { PageTitle, BodyText, MedBodyText, BoldBodyText, SecondaryButton, FlexColumn } from '../components/styles/index.js'
import { tokens } from '../theme.js'
import {
  BillingContent,
  BillingHeader,
  BillingSubtitle,
  BillingCard,
  BillingSplitCard,
  BillingTotal,
  BillingBarTrack,
  BillingBarFill,
  BillingSkeleton,
  BillingRow,
  BillingCategoryRow,
  BillingPanels,
  BillingTenantName
} from './styles/BillingPageStyles.js'

function money(usd, cad, cadPerUsd) {
  const v = cad ? usd * cadPerUsd : usd
  const sym = cad ? 'CA$' : '$'
  if (v === 0) return `${sym}0.00`
  if (v < 0.01) return `${sym}${v.toFixed(6)}`
  return `${sym}${v.toFixed(4)}`
}

const Bar = ({ frac }) => (
  <BillingBarTrack>
    <BillingBarFill $fraction={frac} />
  </BillingBarTrack>
)

export default function BillingPage() {
  const [report, setReport] = useState(null)
  const [cad, setCad] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    api
      .get('/api/billing/report')
      .then(({ data }) => setReport(data))
      .catch(e => setError(errorMessage(e)))
  }, [])

  const fmt = usd => money(usd, cad, report?.cadPerUsd ?? 1.37)
  const maxCat = report ? Math.max(1e-12, ...report.byCategory.map(c => c.costUsd)) : 1

  return (
    <AppShell crumbs={[{ label: 'Projects', to: '/', testId: 'crumb-projects' }, { label: 'Billing' }]}>
      <BillingContent>
        <BillingHeader gap="12px">
          <PageTitle style={{ flex: 1 }}>Usage &amp; Billing</PageTitle>
          <SecondaryButton onClick={() => setCad(v => !v)} data-testid="currency-toggle">
            Show in {cad ? 'USD' : 'CAD'}
          </SecondaryButton>
        </BillingHeader>
        <BillingSubtitle>
          Global operator view across all tenants. Rate card {report?.rateVersion}. Amounts in {cad ? 'CAD' : 'USD'}.
        </BillingSubtitle>

        {error && (
          <BodyText style={{ color: tokens.colors.error }} data-testid="billing-error">
            {error}
          </BodyText>
        )}
        {!report && !error && (
          <FlexColumn gap="20px" style={{ marginTop: 20 }} data-testid="billing-skeleton">
            {[96, 180, 180].map((h, i) => (
              <BillingSkeleton key={i} $height={h} />
            ))}
          </FlexColumn>
        )}

        {report && (
          <FlexColumn gap="20px" style={{ marginTop: 20 }}>
            <BillingCard>
              <MedBodyText style={{ color: tokens.colors.inputField.placeholder }}>Total spend</MedBodyText>
              <BillingTotal data-testid="billing-total">{fmt(report.totalUsd)}</BillingTotal>
            </BillingCard>

            <BillingCard>
              <BoldBodyText style={{ marginBottom: 12 }}>By category</BoldBodyText>
              <FlexColumn gap="12px">
                {report.byCategory.length === 0 && <MedBodyText>No usage recorded yet.</MedBodyText>}
                {report.byCategory.map(c => (
                  <div key={c.category} data-testid="billing-category-row" data-category={c.category}>
                    <BillingCategoryRow gap="12px">
                      <MedBodyText>
                        <b>{c.category}</b> · {c.quantity.toLocaleString()} units
                      </MedBodyText>
                      <MedBodyText data-testid="category-cost">{fmt(c.costUsd)}</MedBodyText>
                    </BillingCategoryRow>
                    <Bar frac={c.costUsd / maxCat} />
                  </div>
                ))}
              </FlexColumn>
            </BillingCard>

            <BillingPanels gap="20px">
              <BillingSplitCard>
                <BoldBodyText style={{ marginBottom: 12 }}>By provider / model</BoldBodyText>
                <FlexColumn gap="8px">
                  {report.byProviderModel.map((p, i) => (
                    <BillingRow key={i} data-testid="billing-provider-row" gap="12px">
                      <MedBodyText>
                        {p.provider} · {p.model} <span style={{ color: tokens.colors.inputField.placeholder }}>({p.category})</span>
                      </MedBodyText>
                      <MedBodyText>{fmt(p.costUsd)}</MedBodyText>
                    </BillingRow>
                  ))}
                </FlexColumn>
              </BillingSplitCard>

              <BillingSplitCard>
                <BoldBodyText style={{ marginBottom: 12 }}>By tenant</BoldBodyText>
                <FlexColumn gap="8px">
                  {report.byTenant.map(t => (
                    <BillingRow key={t.tenantId} data-testid="billing-tenant-row" gap="12px">
                      <BillingTenantName>{t.tenantName || t.tenantId}</BillingTenantName>
                      <MedBodyText>{fmt(t.costUsd)}</MedBodyText>
                    </BillingRow>
                  ))}
                </FlexColumn>
              </BillingSplitCard>
            </BillingPanels>
          </FlexColumn>
        )}
      </BillingContent>
    </AppShell>
  )
}
